import { useEffect, useRef } from 'react';
import { usePlatformTheme } from '@/hooks/usePlatformTheme';

/**
 * A atmosfera do site do DevClub (`devclub-lp/parts/js/01-hero.js`): bancos de
 * névoa verde-petróleo, linhas que ondulam acompanhando a névoa e o véu
 * diagonal. No site ela vive em loop; aqui fica parada no mesmo instante que o
 * site usa para movimento reduzido (t = 11,7 s), porque tela de entrada não
 * tem fundo em loop. O que se mexe é a malha de cruzes, e só com o mouse.
 * Fica de fora o que era do herói do site (a máscara atrás da estátua).
 */
const INSTANTE = 11.7;

const BANCOS = [
  {
    fx: -0.04,
    fy: 0.22,
    r: 900,
    orb: 52,
    vel: 0.016,
    fase: 0.0,
    matiz: '160',
    alfa: 0.24,
  },
  {
    fx: 0.16,
    fy: 0.44,
    r: 760,
    orb: 44,
    vel: 0.021,
    fase: 1.7,
    matiz: '160',
    alfa: 0.2,
  },
  {
    fx: 0.34,
    fy: 0.16,
    r: 640,
    orb: 38,
    vel: 0.013,
    fase: 3.1,
    matiz: '150',
    alfa: 0.16,
  },
  {
    fx: 0.3,
    fy: 0.82,
    r: 820,
    orb: 60,
    vel: 0.018,
    fase: 4.4,
    matiz: '150',
    alfa: 0.15,
  },
  {
    fx: 0.58,
    fy: 0.34,
    r: 560,
    orb: 34,
    vel: 0.024,
    fase: 2.2,
    matiz: '160',
    alfa: 0.11,
  },
  {
    fx: 0.86,
    fy: 0.88,
    r: 600,
    orb: 46,
    vel: 0.011,
    fase: 5.6,
    matiz: '150',
    alfa: 0.09,
  },
];

export function Atmosfera() {
  const ref = useRef<HTMLCanvasElement>(null);
  const { theme } = usePlatformTheme();

  useEffect(() => {
    const cv = ref.current;
    const cx = cv?.getContext('2d');
    if (!cv || !cx) return;
    const claro = theme === 'light';

    const desenhar = () => {
      // meia resolução: névoa e linhas são desfocadas por natureza
      const DPR = Math.min(devicePixelRatio || 1, 1.5) * 0.4;
      const W = (cv.width = Math.round(innerWidth * DPR));
      const H = (cv.height = Math.round(innerHeight * DPR));
      const N = innerWidth < 700 ? 14 : 22;
      const t = INSTANTE;
      // os raios do site são para tela de notebook: no celular a névoa encolhe junto, senão lava a tela
      const escala = Math.min(1, Math.max(0.5, innerWidth / 1200));

      const pos = BANCOS.map((b) => ({
        x: b.fx * W + Math.cos(t * b.vel * 8 + b.fase) * b.orb * DPR * 1.6,
        y: b.fy * H + Math.sin(t * b.vel * 5.6 + b.fase) * b.orb * DPR,
        rx: b.r * DPR * escala,
        a: b.alfa,
        matiz: b.matiz,
      }));

      // densidade da névoa em (x, y): é o que acopla as linhas à névoa
      const densidade = (x: number, y: number) => {
        let s = 0;
        for (const p of pos) {
          const d = 1 - Math.hypot(x - p.x, y - p.y) / p.rx;
          if (d > 0) s += d * d * p.a;
        }
        return s;
      };

      cx.clearRect(0, 0, W, H);
      cx.globalCompositeOperation = claro ? 'source-over' : 'lighter';

      for (const p of pos) {
        const g = cx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.rx);
        g.addColorStop(0, `oklch(${claro ? '0.38 0.05' : '0.45 0.07'} ${p.matiz} / ${p.a})`);
        g.addColorStop(1, 'transparent');
        cx.fillStyle = g;
        cx.fillRect(p.x - p.rx, p.y - p.rx, p.rx * 2, p.rx * 2);
      }

      cx.lineCap = 'round';
      cx.lineJoin = 'round';
      const PASSO = 64 * DPR;
      for (let i = 0; i < N; i++) {
        const p = i / (N - 1);
        cx.lineWidth = (0.7 + 0.7 * p) * DPR;
        const baseY = H * (0.46 + 0.52 * p);
        const amp = (4 + 21 * p) * DPR;
        const desloca = t * (0.5 + 1.1 * p);
        const af = p > 0.6 ? 0.045 + 0.065 * p : 0.03 + 0.05 * p;
        const tom = claro ? '0.38 0.09 145' : p > 0.6 ? '0.72 0.10 140' : '0.70 0.07 150';
        const yy = (u: number) =>
          baseY +
          Math.sin(u * 4.1 + desloca * 2.0 + p * 2.6) * amp * 0.55 +
          Math.sin(u * 9.7 - desloca * 1.3 + p * 5.2) * amp * 0.3 +
          Math.sin(u * 2.3 + desloca * 0.7) * amp * 0.45 +
          (u - 0.5) * H * 0.11; // caimento de ~6°: não lê como veneziana
        let px = 0;
        let py = yy(0);
        for (let x = PASSO; x <= W; x += PASSO) {
          const u = x / W;
          const ny = yy(u);
          const dens = densidade((px + x) / 2, (py + ny) / 2);
          // a luz entra pela esquerda e morre à direita
          const dir =
            u < 0.28
              ? 0.55 + 1.6 * u
              : u < 0.66
                ? 1
                : Math.max(0.1, 1 - (u - 0.66) * 2.6);
          const a = af * dir * (0.3 + 0.7 * Math.min(1, dens * 5.5)) * (claro ? 2.5 : 1);
          if (a > 0.004) {
            cx.strokeStyle = `oklch(${tom} / ${a})`;
            cx.beginPath();
            cx.moveTo(px, py);
            cx.lineTo(x, ny);
            cx.stroke();
          }
          px = x;
          py = ny;
        }
      }

      // o véu diagonal, parado onde o instante o deixa
      const ciclo = (t % 18) / 18;
      const vx = (ciclo * 2.4 - 0.7) * W;
      const g2 = cx.createLinearGradient(
        vx - W * 0.17,
        0,
        vx + W * 0.17,
        H * 0.35,
      );
      g2.addColorStop(0, 'transparent');
      g2.addColorStop(0.5, claro ? 'oklch(0.42 0.06 140 / .12)' : 'oklch(0.62 0.07 140 / .085)');
      g2.addColorStop(1, 'transparent');
      cx.fillStyle = g2;
      cx.save();
      cx.translate(vx, H * 0.5);
      cx.rotate(-0.6);
      cx.translate(-vx, -H * 0.5);
      cx.fillRect(vx - W * 0.3, -H * 0.3, W * 0.6, H * 1.6);
      cx.restore();

      cx.globalCompositeOperation = 'source-over';
    };

    desenhar();
    addEventListener('resize', desenhar, { passive: true });
    return () => removeEventListener('resize', desenhar);
  }, [theme]);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 h-screen w-screen"
    />
  );
}
